<template>
  <div class="uk-grid-medium" uk-grid>
    <div class="uk-width-5-5">
      <div class="d-flex">
        <nav id="breadcrumbs" class="mb-3">
          <ul>
            <li>
              <router-link :to="{ name: 'company.dashboard' }">
                <i class="uil-home-alt"></i>
              </router-link>
            </li>
            <li>
              {{ this.$t('menu.company_dashboard.assignments') }}
            </li>
            <li>{{ this.$t(pageTitle) }}</li>
          </ul>
        </nav>
      </div>

      <div class="section-header mb-lg-2 border-0 uk-flex-middle">
        <div class="section-header-left">
          <h2 class="uk-heading-line text-left">
            <span> {{ this.$t(pageTitle) }} ({{ totalItems }}) </span>
          </h2>
        </div>
        <div v-if="statusFilter" class="section-header-right">
          <router-link :to="{ name: 'company.assignments.add' }">
            <button class="btn btn-default" type="button" aria-expanded="false"
              :disabled="!$hasPermission('create_assignment')"
              :style="{ cursor: !$hasPermission('create_assignment') ? 'not-allowed' : 'pointer' }">
              <i class="uil-location-arrow"></i> {{ this.$t('menu.company_dashboard.create_assignment') }}
            </button>
          </router-link>
        </div>
      </div>

      <template v-if="errorMessages.length">
        <div v-for="(error, index) in errorMessages" :key="index" class="uk-alert-danger" uk-alert>
          <a class="uk-alert-close" uk-close></a>
          <p style="color: red">
            <i class="uil-exclamation-triangle"></i> {{ error }}
          </p>
        </div>
      </template>
      <div v-if="successMessage" class="uk-alert-success" uk-alert>
        <a class="uk-alert-close" uk-close></a>
        <p style="color: green">
          <i class="uil-exclamation-triangle"></i> {{ successMessage }}
        </p>
      </div>
      <div v-if="viewMode === 'grid'" class="section-header pb-0">
        <div class="section-header-left">
          <input type="text" :placeholder="$t('general.search')" v-model="filter.search" />
        </div>
        <div class="section-header-right assignment-toolbar">
          <multiple-select-input v-model="selectedViewMode" :allowEmpty="false" :multiple="false" :required="false"
            :searchable="false" :showLabels="false" :values="viewModeOptions" class="view-mode-select"
            @select="changeViewMode" />
          <multiple-select-input v-model="selectedSort" :allowEmpty="false" :multiple="false" :required="false"
            :searchable="false" :showLabels="false" :values="sortOptions" class="order-select" @select="sortOrder" />
        </div>
      </div>

      <div class="pb-4 uk-grid justify-content-between" uk-grid>
        <div class="uk-width-3-4@m uk-first-column">
          <div v-if="viewMode === 'grid'" class="uk-child-width-1-3@m mt-4 uk-grid uk-first-column assignment-grid"
            uk-grid uk-scrollspy="target: > div; cls: uk-animation-slide-left-small; delay: 200">
            <div v-for="assignment in displayedAssignments" :key="assignment.id">
              <div class="card animate-this uk-inline-clip assignment-card">
                <div class="position-relative">
                  <img :src="assignment.cover_image_link" alt=""
                    style="min-height: 200px; max-height: 200px; width: 100%" />
                  <span v-if="!assignment.status_id" class="badge badge-danger position-absolute"
                    style="top: 10px; right: 10px; font-size: 0.75rem; padding: 0.35rem 0.65rem">
                    {{ $t("general.passive") }}
                  </span>
                </div>
                <div class="assignment-card-content">
                  <div class="card-body text-center pb-3 assignment-card-heading">
                    <h6 class="mb-2 assignment-name">{{ assignment.name }}</h6>
                    <h6 v-if="assignment.repeatable" class="mb-0 assignment-order">{{ assignment.order_number }}</h6>
                  </div>
                </div>
                <div class="card-footer py-3 border-top-0 assignment-card-footer">
                  <div class="assignment-card-actions d-flex flex-column">
                    <router-link :to="{ name: 'company.assignments.preview', params: { id: assignment.id } }"
                      class="btn btn-secondary btn-sm mb-2">
                      <i class="uil-eye"></i>{{ $t("certification.preview") }}
                    </router-link>
                    <router-link :to="{ name: 'company.assignments.edit', params: { id: assignment.id } }"
                      class="btn btn-info btn-sm mb-2">
                      <i class="uil-play-circle"></i> {{ $t("faq_categories.edit") }}
                    </router-link>
                    <a href="#" class="btn btn-danger btn-sm mb-3" :disabled="!$hasPermission('delete_assignment')"
                      :style="{ cursor: !$hasPermission('delete_assignment') ? 'not-allowed' : 'pointer' }"
                      @click.prevent="removeAssignment(assignment.id)">
                      <i class="uil-minus-circle"></i> {{ $t("general.delete") }}
                    </a>
                  </div>
                  <div class="row align-items-center text-center">
                    <div class="col-12 py-3">
                      <a :uk-tooltip="`title: ${$t('general.start_end_date')}; pos: bottom-center`" class="text-muted">
                        <i class="uil-calendar-alt"></i>
                        {{ assignment.start_end }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="card mt-4">
            <div class="card-header actions-toolbar border-0 users-toolbar assignments-toolbar">
              <div class="d-flex justify-content-between users-toolbar-inner">
                <div class="d-flex users-toolbar-search assignments-toolbar-search-group">
                  <div class="uk-search uk-search-navbar assignments-toolbar-search">
                    <i class="uil-search position-absolute"></i>
                    <input class="uk-search-input shadow-0 uk-form-small" type="search" v-model="filter.search"
                      :placeholder="$t('general.search')" />
                  </div>
                  <multiple-select-input v-model="selectedViewMode" :allowEmpty="false" :multiple="false"
                    :required="false" :searchable="false" :showLabels="false" :values="viewModeOptions"
                    class="view-mode-select" @select="changeViewMode" />
                </div>
                <div class="d-flex users-toolbar-actions">
                  <multiple-select-input v-model="selectedSort" :allowEmpty="false" :multiple="false" :required="false"
                    :searchable="false" :showLabels="false" :values="sortOptions" class="order-select"
                    @select="sortOrder" />
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-between mr-3 ml-3 mt-3 assignments-toolbar-summary">
              <div>
                <span class="btn btn-outline-dark btn-sm mr-2">{{ $t("general.assignments_total") }} : {{ totalItems
                }}</span>
                <span class="btn btn-outline-dark btn-sm mr-2">{{ $t("general.selected_assignments") }} : {{
                  selectedAssignmentIds.length }}</span>
              </div>
              <div>{{ $t(statusFilter ? "general.active_assignments" : "general.passive_assignments") }}</div>
            </div>

            <div class="p-3 d-flex disabled_hover users-bulk-actions assignment-bulk-actions">
              <button type="button" class="btn btn-sm btn-secondary mr-2 mr-auto p-2" @click="toggleAllVisibleButton">
                <i class="uil-check-square"></i> {{ $t(allVisibleSelected ? "general.deselect_all" : "general.select_all")
                }}
              </button>
              <button v-if="isPassiveList" class="btn btn-sm btn-success" type="button"
                :disabled="!hasSelection || assignment_loading" @click="bulkUpdateStatus(true)">
                <i class="uil-check"></i> {{ $t("general.activate_selected_users") }}
              </button>
              <button v-if="!isPassiveList" class="btn btn-sm btn-danger ml-2" type="button"
                :disabled="!hasSelection || assignment_loading" @click="bulkUpdateStatus(false)">
                <i class="uil-minus-circle"></i> {{ $t("general.inactive_selected_users") }}
              </button>
              <button class="btn btn-sm btn-secondary ml-2" type="button" :disabled="!hasSelection || assignment_loading"
                @click="bulkDeleteAssignments">
                <i class="uil-trash"></i> {{ $t("general.delete_selected_users") }}
              </button>
            </div>

            <div class="table-responsive">
              <table class="table align-items-center assignment-list-table">
                <thead>
                  <tr>
                    <th scope="col"></th>
                    <th scope="col">{{ $t("general.assignment") }}</th>
                    <th scope="col">{{ $t("general.status") }}</th>
                    <th scope="col">{{ $t("general.date") }}</th>
                    <th scope="col"></th>
                  </tr>
                </thead>
                <tbody class="list">
                  <tr v-for="assignment in displayedAssignments" :key="`list-${assignment.id}`">
                    <th>
                      <input :checked="isAssignmentSelected(assignment.id)" :id="'assignment_checkbox_' + assignment.id"
                        :value="assignment.id" style="width: 15px; margin: 10px 10px 10px 0" type="checkbox"
                        @change="toggleAssignmentSelection(assignment.id, $event.target.checked)" />
                    </th>
                    <th scope="row">
                      <div class="media align-items-center">
                        <div class="assignment-cover-thumb">
                          <img :src="assignment.cover_image_link" alt="" />
                        </div>
                        <div class="media-body ml-4">
                          <div class="name h6 mb-0 text-sm">{{ assignment.name }}</div>
                          <small class="d-block font-weight-bold">#{{ assignment.id }}</small>
                          <small class="d-block assignment-list-table__meta">{{ assignment.description }}</small>
                        </div>
                      </div>
                    </th>
                    <td :class="assignment.status_id ? 'text-success' : 'text-danger'">
                      {{ assignment.status_id ? $t("general.active") : $t("general.passive") }}
                    </td>
                    <td>{{ assignment.start_end }}</td>
                    <td class="text-right">
                      <div class="actions ml-3">
                        <router-link :to="{ name: 'company.assignments.preview', params: { id: assignment.id } }"
                          class="btn btn-icon btn-hover btn-sm btn-circle" :uk-tooltip="$t('certification.preview')">
                          <i class="uil-eye"></i>
                        </router-link>
                        <router-link :to="{ name: 'company.assignments.edit', params: { id: assignment.id } }"
                          class="btn btn-icon btn-hover btn-sm btn-circle" :uk-tooltip="$t('faq_categories.edit')">
                          <i class="uil-play-circle"></i>
                        </router-link>
                        <a v-if="isPassiveList" href="#" class="btn btn-icon btn-hover btn-sm btn-circle"
                          :uk-tooltip="$t('general.activate_assignment')"
                          @click.prevent="bulkUpdateSingleAssignment(assignment)">
                          <i class="uil-check text-success"></i>
                        </a>
                        <a v-else href="#" class="btn btn-icon btn-hover btn-sm btn-circle"
                          :uk-tooltip="$t('general.deactivate_assignment')"
                          @click.prevent="bulkUpdateSingleAssignment(assignment)">
                          <i class="uil-minus-circle text-danger"></i>
                        </a>
                        <a href="#" class="btn btn-icon btn-hover btn-sm btn-circle"
                          :disabled="!$hasPermission('delete_assignment')"
                          :style="{ cursor: !$hasPermission('delete_assignment') ? 'not-allowed' : 'pointer' }"
                          :uk-tooltip="$t('general.delete')" @click.prevent="removeAssignment(assignment.id)">
                          <i class="uil-trash text-danger"></i>
                        </a>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="!displayedAssignments.length">
                    <td colspan="7" class="text-center py-4">{{ $t("general.no_assignments_found") }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="row">
            <div class="col-12 text-center">
              <pagination v-if="pagination && pagination.hasOwnProperty('links')" :pagination="pagination"
                @changePage="setPage($event)"></pagination>
            </div>
          </div>
        </div>
        <div class="uk-width-expand">
          <div class="sidebar-filter card p-2 uk-sticky mt-4" uk-sticky="offset:80 ; media : @s: bottom: true">
            <div class="sidebar-filter-contents">
              <h4 style="text-transform: capitalize">
                <i class="il-filter" /> {{ $t("general.filter") }}
              </h4>
              <label>
                <input :data-id="'all'" checked class="uk-radio" data-type="all" name="filters" type="radio"
                  @change="setType" />
                <span style="margin-left: 6px">{{ $t("general.all") }}</span>
              </label>

              <ul class="sidebar-filter-list uk-accordion" uk-accordion="multiple: true">
                <li class="uk-open">
                  <a class="uk-accordion-title" href="#">{{ $t("general.by_category_tag") }}</a>
                  <div aria-hidden="false" class="uk-accordion-content">
                    <div class="form-group">
                      <label for="search-tag" style="font-size: 0.75rem"><i class="uil-search"></i>
                        {{ $t("general.tag_search") }}</label>
                      <input id="search-tag" v-model="searchTag" :placeholder="$t('general.type_a_tag')"
                        autocomplete="off" class="form-control mb-0" type="text" />
                    </div>
                    <div class="uk-form-controls" style="overflow-y: scroll; height: 10rem">
                      <label v-for="(item, index) in filteredTags" :key="`filteredTags-${index}`">
                        <input :data-id="item.id" class="uk-radio" data-type="hashtag" name="filters" type="radio"
                          @change="setType" />
                        <span class="test">{{ $t(item.name) }} </span>
                      </label>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div class="uk-sticky-placeholder" hidden="" style="height: 721px; margin: 0 0 20px"></div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import Vue from "vue";
import DataTable from "@/assets/components/dataTable/DataTable";
import store from "@/core/services";
import Pagination from "@/assets/components/Pagination";
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput";

import module, {
  BASE_URL,
  MODULE_NAME,
  ERROR,
  ITEMS,
  GET_ITEMS,
  DELETE_ITEM,
  LOADING,
} from "@/core/services/store/assignment.module";
import moment from "moment";
import Swal from "sweetalert2";
import { RESET_POINTS } from "@/core/services/store/company.module";
import axios from "axios";
import AppMultiselect from "@/view/components/select/app-multiselect/app-multiselect.vue";
import _ from "lodash";

export default {
  name: "AssignmentList",
  components: { DataTable, Pagination, DefaultLoading, MultipleSelectInput, AppMultiselect },
  data() {
    return {
      searchTag: "",
      searchTagList: [],
      items: [],
      totalItems: 0,
      pagination: null,
      successMessage: null,
      errorMessages: [],
      page: 1,
      per_page: 12,
      viewMode: "grid",
      selectedViewMode: null,
      selectedAssignmentIds: [],
      filter: {
        search: null,
        sort: "desc",
        order_by: "created_at",
        hashtag_id: null
      },
      selectedSort: null,
    };
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }
    registerStoreModule(MODULE_NAME, module);
  },
  computed: {
    filteredTags() {
      const tags = this.searchTagList;
      const search = this.searchTag;
      if (search === "" || tags == null) return tags;

      return tags.filter((tag) => tag.name.toLowerCase().includes(search.toLowerCase()));
    },
    displayedAssignments() {
      return Array.isArray(this.items) ? this.items : [];
    },
    sortOptions() {
      return [
        { id: "name_asc", name: this.$t("sorting.a_z"), sort: "asc", order_by: "name" },
        { id: "name_desc", name: this.$t("sorting.z_a"), sort: "desc", order_by: "name" },
        { id: "created_at_asc", name: this.$t("sorting.created_at_asc"), sort: "asc", order_by: "created_at" },
        { id: "created_at_desc", name: this.$t("sorting.created_at_desc"), sort: "desc", order_by: "created_at" },
        { id: "end_date_asc", name: this.$t("sorting.end_date_asc"), sort: "asc", order_by: "end_date" },
        { id: "end_date_desc", name: this.$t("sorting.end_date_desc"), sort: "desc", order_by: "end_date" },
        { id: "start_date_asc", name: this.$t("sorting.start_date_asc"), sort: "asc", order_by: "start_date" },
        { id: "start_date_desc", name: this.$t("sorting.start_date_desc"), sort: "desc", order_by: "start_date" },
      ];
    },
    viewModeOptions() {
      return [
        { id: "grid", name: this.$t("general.card_view") },
        { id: "list", name: this.$t("general.list_view") },
      ];
    },
    assignment_loading: {
      get() {
        return store.getters[MODULE_NAME + "/" + LOADING];
      },
      set() { },
    },
    error: {
      get() {
        return store.getters[MODULE_NAME + "/" + ERROR];
      },
      set() { },
    },
    statusFilter() {
      const query = this.$route.query || {};
      if (this.$route.path.includes("assignments-passive")) return 0;
      const isPassive = "passive" in query || query.status === "passive";
      return isPassive ? 0 : 1;
    },
    pageTitle() {
      return this.$route.path.includes("assignments-passive") ? "general.passive_assignments" : "general.all_assignments";
    },
    isPassiveList() {
      return this.statusFilter === 0;
    },
    hasSelection() {
      return this.selectedAssignmentIds.length > 0;
    },
    allVisibleSelected() {
      return this.displayedAssignments.length > 0 &&
        this.displayedAssignments.every((assignment) => this.selectedAssignmentIds.includes(assignment.id));
    },

  },
  methods: {
    changeViewMode(option) {
      this.viewMode = option.id;
      this.clearSelection();
    },
    clearSelection() {
      this.selectedAssignmentIds = [];
    },
    isAssignmentSelected(id) {
      return this.selectedAssignmentIds.includes(id);
    },
    toggleAssignmentSelection(id, checked) {
      if (checked) {
        if (!this.selectedAssignmentIds.includes(id)) {
          this.selectedAssignmentIds = [...this.selectedAssignmentIds, id];
        }
        return;
      }

      this.selectedAssignmentIds = this.selectedAssignmentIds.filter((itemId) => itemId !== id);
    },
    toggleAllVisibleButton() {
      if (this.allVisibleSelected) {
        this.clearSelection();
        return;
      }

      this.selectedAssignmentIds = this.displayedAssignments.map((assignment) => assignment.id);
    },
    setType(event) {
      const target = event.target;
      const id = target.getAttribute("data-id");
      const type = target.getAttribute("data-type");
      this.filter.file_type_id = this.filter.content_type_id = this.filter.hashtag = null;
      if (type === "hashtag") this.filter.hashtag_id = id;
      else if (type === "file_type_id") this.filter.file_type_id = id;
      else if (type === "content_type_id") this.filter.content_type_id = id;
      else if (type === "all") {
        this.filter.hashtag_id = null;
        this.getAssignmentResult();
      }
      if (type !== "all") this.debouncedFilterType();
    },
    async getFilteredTags() {
      const { data } = await axios.get("/api/assignment/filters?by_user=false");
      this.searchTagList = data;
    },
    formattedItem() {
      const results = [];
      if (this.items === null || !("data" in this.items)) return [];
      this.pagination = this.items;
      this.totalItems = this.items?.total || 0;
      this.items.data.forEach((currentItem) => {
        results.push({
          id: currentItem.id,
          name: currentItem.name,
          hasAssigned: currentItem.users_count,
          cover_image_link: currentItem.cover_image_link,
          description: currentItem.description || "-",
          order_number: currentItem.repeatable_main_item ? this.$t("general.main_content_assignment") : `${currentItem.order_number}. ${this.$t("general.repeated_assignment")}`,
          repeatable: !!(currentItem.repeatable_main_item || currentItem.repeatable),
          start_end: currentItem.start_date && currentItem.end_date
            ? moment(currentItem.start_date).format("DD-MM-YYYY") + " / " + moment(currentItem.end_date).format("DD-MM-YYYY")
            : "-",
          status_id: currentItem.status_id == true || currentItem.status_id === 1 || currentItem.status_id === "1",
        });
      });
      this.items = results;
      return results;
    },
    getAssignmentResult() {
      this.$store.dispatch(MODULE_NAME + "/" + GET_ITEMS, {
        url: BASE_URL,
        filters: {
          search: this.filter.search,
          page: this.page,
          per_page: this.per_page,
          sort: this.filter.sort,
          order_by: this.filter.order_by,
          with_users: true,
          status_id: this.statusFilter,
          hashtag_id: this.filter.hashtag_id,
        },
      }).then((result) => {
        this.items = result;
        this.formattedItem();
        this.clearSelection();
      });
    },
    async bulkUpdateStatus(status) {
      if (!this.hasSelection) return;

      const ids = this.selectedAssignmentIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id));
      // const result = await Swal.fire({
      //   title: this.$t(status ? "general.bulk_activate_assignments_confirm" : "general.bulk_deactivate_assignments_confirm", { count: ids.length }),
      //   icon: "question",
      //   showCancelButton: true,
      //   confirmButtonText: this.$t("general.yes"),
      //   cancelButtonText: this.$t("general.cancel"),
      //   confirmButtonColor: status ? "#28a745" : "#dc3545",
      // });
      //
      // if (!result.isConfirmed) return;

      try {
        await axios.put("/api/assignments/bulk-update", { ids, status });
        this.successMessage = this.$t(status ? "general.selected_assignments_activated" : "general.selected_assignments_deactivated");
        this.getAssignmentResult();
      } catch (error) {
        this.errorMessages.push(error?.response?.data?.message || error?.message);
      }
    },
    bulkUpdateSingleAssignment(assignment) {
      this.selectedAssignmentIds = [assignment.id];
      this.bulkUpdateStatus(!assignment.status_id);
    },
    async bulkDeleteAssignments() {
      if (!this.hasSelection) return;

      const ids = this.selectedAssignmentIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id));
      const result = await Swal.fire({
        title: this.$t("general.sure_to_delete", { count: ids.length }),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("general.yes"),
        cancelButtonText: this.$t("general.cancel"),
        confirmButtonColor: "#e31748",
      });

      if (!result.isConfirmed) return;

      try {
        await axios.delete("/api/assignments/bulk-destroy", { data: { ids } });
        this.successMessage = this.$t("general.successfully_deleted");
        this.getAssignmentResult();
      } catch (error) {
        this.errorMessages.push(error?.response?.data?.message || error?.message);
      }
    },
    removeAssignment(id) {
      Swal.fire({
        title: this.$t("general.assignment_delete_with_users_confirm"),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t("general.yes"),
        cancelButtonText: this.$t("general.cancel"),
        confirmButtonColor: "#e31748",
      }).then((result) => {
        if (!result.isConfirmed) return;

        this.$store.dispatch(MODULE_NAME + "/" + DELETE_ITEM, {
          url: BASE_URL + "/" + id,
        }).then(() => {
          if (!this.error) {
            this.successMessage = this.$t("general.successfully_deleted");
            this.getAssignmentResult();
          } else {
            this.errorMessages.push(this.error);
          }
        });
      });
    },
    sortOrder(value) {
      this.page = 1;
      this.filter.sort = value.sort;
      this.filter.order_by = value.order_by;
      this.getAssignmentResult();
    },
    setPage(page) {
      this.page = page;
      this.getAssignmentResult();
    },
    resetMessages() {
      this.successMessage = null;
      this.errorMessages = [];
    },
  },
  mounted() {
    this.selectedSort = this.sortOptions.find((o) => o.id === "created_at_desc");
    this.selectedViewMode = this.viewModeOptions.find((option) => option.id === this.viewMode);
    this.getAssignmentResult();
    this.getFilteredTags();
  },
  watch: {
    "$i18n.locale"() {
      this.selectedSort = this.sortOptions.find((option) => option.id === this.selectedSort?.id) || this.sortOptions[0];
      this.selectedViewMode = this.viewModeOptions.find((option) => option.id === this.viewMode) || this.viewModeOptions[0];
    },
    $route() {
      this.page = 1;
      this.getAssignmentResult();
    },
    "filter.search": function () {
      this.debouncedFilterSearch();
    },
    successMessage: function () {
      this.debouncedResetValues();
    },
    errorMessages: function () {
      this.debouncedResetValues();
    },
  },
  created: function () {
    this.debouncedFilterSearch = _.debounce(this.getAssignmentResult, 500);
    this.debouncedResetValues = _.debounce(this.resetMessages, 2000);
    this.debouncedFilterType = _.debounce(this.getAssignmentResult, 300);
  },
};
</script>
<style scoped>
input[type="button"],
input[type="submit"],
span.btn,
.btn {
  display: inline-block;
}

.assignment-grid>div {
  display: flex;
}

.assignment-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

.assignment-card-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
}

.assignment-card-heading {
  min-height: 0;
}

.assignment-name {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.assignment-card-footer {
  margin-top: auto;
}

.assignment-card-actions {
  padding: 0 1rem;
}

.assignment-order {
  font-size: 12px;
  color: rgb(149, 149, 149);
}

.assignment-toolbar {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.assignments-toolbar-search-group {
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.assignments-toolbar-search {
  flex: 1;
}

.assignments-toolbar-summary {
  font-size: 14px;
  align-items: center;
}

.assignment-bulk-actions {
  flex-wrap: wrap;
  gap: 8px;
}

.assignment-cover-thumb {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  overflow: hidden;
  background: #f1f5f9;
  flex-shrink: 0;
}

.assignment-cover-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.assignment-list-table__meta {
  color: #6b7280;
  font-size: 12px;
  max-width: 320px;
  white-space: normal;
}

.assignments-toolbar ::v-deep .form-group,
.section-header-right ::v-deep .form-group {
  margin-bottom: 0;
}

.order-select,
.view-mode-select {
  min-width: 220px;
}

.assignments-toolbar ::v-deep .multiselect__single,
.section-header-right ::v-deep .multiselect__single {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.assignments-toolbar ::v-deep .multiselect__content-wrapper,
.section-header-right ::v-deep .multiselect__content-wrapper {
  max-height: none !important;
}

@media (max-width: 991px) {
  .assignments-toolbar-summary {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
}

@media (max-width: 767px) {

  .assignment-toolbar,
  .users-toolbar-inner,
  .users-toolbar-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .users-toolbar-search,
  .assignments-toolbar-search-group,
  .assignments-toolbar-search,
  .order-select,
  .view-mode-select {
    width: 100%;
    min-width: 100%;
  }

  .assignment-bulk-actions .btn {
    width: 100%;
    margin: 0 !important;
  }
}</style>
